import { Component, OnInit, ViewChild } from '@angular/core';
import { ChartComponent, ApexOptions } from 'ng-apexcharts';
import { NGXLogger } from 'ngx-logger';
import { ForecastChartService } from '@reenstudio/app/core/services/charts/forecast-service';
import { ForecastChartApiService } from '@reenstudio/app/core/services/charts-api/forecast-api-service';
import {
  ForecastChartApiResponse,
  forecastChartObj,
} from '@reenstudio/app/core/models/charts/forecast-model';
import { Subscription, of, switchMap } from 'rxjs';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { UserRole } from '@reenstudio/app/core/enum/user-role';
import { EventService } from '@reenstudio/app/core/services/event-service';
import { SiteService } from '@reenstudio/app/core/services/site-service';

@Component({
  selector: 'app-forecast-lines',
  templateUrl: './forecast-lines.component.html',
  styleUrls: ['./forecast-lines.component.scss'],
})
export class ForecastLinesChartComponent implements OnInit {
  @ViewChild('chart') chart!: ChartComponent;
  public chartOptions: Partial<ApexOptions>;

  loading: boolean = false;
  selectedSite: number = 1;
  selectedSiteName: string;

  forecastChartObj: forecastChartObj = {
    consumato: [],
    prodotto: [],
    ore: [],
    formattedDates: [],
    overWriteFormattedDates: [],
    series: [],
    start: new Date(),
    end: new Date(),
  };

  cer: number = 0;
  site: number = 0;

  dataSubscription: Subscription;
  siteSubscription: Subscription;
  cerSubscription: Subscription;

  userLevel: UserRole;

  constructor(
    private logger: NGXLogger,
    private chartService: ForecastChartService,
    private forecastApiService: ForecastChartApiService,
    private siteService: SiteService,
    private storageService: StorageService
  ) {
    this.chartService.setForecastChart(this.forecastChartObj);
  }

  ngOnInit(): void {
    if (this.storageService.getLevel() === UserRole.SITE) {
      this.siteService.setSelectedSite().subscribe((selectedSite: any) => {
        this.selectedSite = selectedSite.id;
      });
    }

    this.getData();

    this.cerSubscription = EventService.changeCer.subscribe(() => {
      const cer = this.storageService.getCer();
      if (cer) {
        this.cer = cer;
        this.getData();
      }
    });

    this.siteSubscription = EventService.changeSite.subscribe(() => {
      const site = this.storageService.getSite();
      if (site) {
        this.site = site;
      }
      this.getData();
    });

    this.siteService.setSelectedSite().subscribe((selectedSite: any) => {
      this.selectedSite = selectedSite.id;
      this.selectedSiteName = selectedSite.name;
    });

    this.getData();
  }

  getData() {
    this.loading = true;
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate());

    const end = new Date(today);
    end.setDate(end.getDate() + 7); // Domani + 7 giorni = una settimana

    if (this.storageService.getLevel() === UserRole.SITE) {
      if (this.site === 0) {
        this.site = this.selectedSite;
      }
    } else {
      if (this.cer === 0) {
        const cerId = this.storageService.getCer();
        if (cerId) {
          this.cer = cerId;
        }
      }
    }

    this.dataSubscription = this.forecastApiService
      .getForecast(start, end, this.cer, this.site)
      .pipe(
        switchMap((apiData: ForecastChartApiResponse[]) => {
          this.logger.debug('Forecast Chart API data', apiData);

          this.forecastChartObj = {
            consumato: [],
            prodotto: [],
            ore: [],
            formattedDates: [],
            overWriteFormattedDates: [],
            series: [],
            start: new Date(),
            end: new Date(),
          };

          this.chartOptions = this.chartService.setForecastChart(
            this.forecastChartObj
          );

          // Esegui i calcoli necessari
          const res = this.chartService.getForecastData(
            apiData,
            this.forecastChartObj
          );

          this.forecastChartObj = res;

          this.logger.debug('Forecast Chart managed data', res);

          // Ritorna un Observable di completamento
          return of(null);
        })
      )
      .subscribe(() => {
        this.loading = false;
        this.chartOptions = this.chartService.setForecastChart(
          this.forecastChartObj
        );
        this.logger.debug('Forecast chartOptions data', this.chartOptions);
      });
  }

  ngOnDestroy() {
    this.dataSubscription.unsubscribe();
    this.siteSubscription.unsubscribe();
    this.cerSubscription.unsubscribe();
  }
}
